import React, { useCallback, useEffect, useState } from 'react';
import { Phone, Mail, MapPin, CalendarDays, Receipt, FileText, ClipboardCheck, Star, LifeBuoy, StickyNote, BellRing, Plus, Check, Trash2, Loader2, UserRound, AlertTriangle, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Skeleton } from '../../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { easerGet, easerPost, easerPut, easerDelete, errorMessage, fmtDate, fmtTime } from '../../../lib/easerApi';
import { StatusBadge, QUOTE_STATUS, TICKET_STATE, fmtEur, fmtEurCents, fmtDay, fmtDayYear, fmtDateTime, telHref, EmptyLine } from './crmShared';
import { StarRating, fmtAvg } from '../qualite/qcShared';
import { QualityDetailDialog } from '../qualite/QualityDetailDialog';
import { RevenueWidget } from './RevenueWidget';

const SECTIONS = [
  { id: 'suivi', label: 'Suivi', icon: StickyNote },
  { id: 'interventions', label: 'Interventions', icon: CalendarDays },
  { id: 'devis', label: 'Devis', icon: FileText },
  { id: 'factures', label: 'Factures', icon: Receipt },
  { id: 'qualite', label: 'Qualité & avis', icon: Star },
  { id: 'tickets', label: 'Réclamations', icon: LifeBuoy },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
/** Fiche client complète du CRM (plein écran sur mobile) : coordonnées, notes & rappels, historique. */
export function ClientFileDialog({ socid, clientName, open, onClose, onUnauthorized, userId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [section, setSection] = useState('suivi');
  const [qcOpen, setQcOpen] = useState(null);

  const load = useCallback(async () => {
    if (!socid) return;
    setError('');
    try { setData((await easerGet(`/api/easer/crm/clients/${socid}`)).data); } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError(errorMessage(err, 'Fiche client indisponible')); setData({});
    }
  }, [socid, onUnauthorized]);

  useEffect(() => { if (open) { setData(null); setSection('suivi'); load(); } }, [open, load]);

  const c = data?.client;
  const notes = data?.notes || [];
  const openReminders = notes.filter((n) => n.remind_at && !n.done).length;
  const counts = {
    suivi: notes.length, interventions: (data?.interventions?.upcoming?.length || 0) + (data?.interventions?.past?.length || 0), devis: data?.quotes?.length || 0,
    factures: data?.invoices?.count || 0, qualite: (data?.controls?.length || 0) + (data?.ratings?.length || 0), tickets: data?.tickets?.length || 0,
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg w-[96vw] max-h-[94vh] overflow-y-auto overflow-x-hidden rounded-2xl p-4 [&>*]:min-w-0" data-testid="crm-client-file">
        <DialogHeader className="text-left space-y-1">
          <DialogTitle className="font-['Space_Grotesk'] pr-6 flex items-center gap-2"><UserRound className="h-5 w-5 text-[hsl(var(--primary))]" /> <span data-testid="crm-client-name">{c?.name || clientName || 'Client'}</span></DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm">
              {c && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 min-w-0 [&>*]:max-w-full [&>*]:break-all">
                  {(c.address || c.town) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[c.address, [c.zip, c.town].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span>}
                  {c.phone && <a href={telHref(c.phone)} className="inline-flex items-center gap-1 text-[hsl(var(--primary))] font-medium" data-testid="crm-client-phone"><Phone className="h-3.5 w-3.5" /> {c.phone}</a>}
                  {c.phone_mobile && c.phone_mobile !== c.phone && <a href={telHref(c.phone_mobile)} className="inline-flex items-center gap-1 text-[hsl(var(--primary))] font-medium"><Phone className="h-3.5 w-3.5" /> {c.phone_mobile}</a>}
                  {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-[hsl(var(--primary))] font-medium truncate"><Mail className="h-3.5 w-3.5" /> {c.email}</a>}
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {!data && <div className="space-y-2"><Skeleton className="h-10 rounded-xl" /><Skeleton className="h-32 rounded-xl" /><Skeleton className="h-24 rounded-xl" /></div>}
        {error && <p className="text-sm text-destructive" role="alert" data-testid="crm-client-error">{error}</p>}
        {data && c && (
          <div className="space-y-3 min-w-0">
            <div className="flex flex-wrap gap-1.5 text-xs" data-testid="crm-client-flags">
              <Badge variant="outline" className="text-[10px]">{data.account?.exists ? `Espace client actif${data.account.revoked ? ' (révoqué)' : ''}` : 'Pas d\'Espace Client'}</Badge>
              {data.invoices?.unpaid_count > 0 && <Badge className={`border text-[10px] ${data.invoices.overdue_count ? 'bg-destructive/10 text-destructive border-destructive/30' : 'bg-[hsl(var(--brand-apricot))]/20 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-apricot))]/60'}`}><AlertTriangle className="h-3 w-3 mr-1" /> {data.invoices.unpaid_count} facture(s) impayée(s) · {fmtEurCents(data.invoices.unpaid_total)}</Badge>}
              {data.annual_cap?.cap && <Badge variant="outline" className="text-[10px]">SAP {data.annual_cap.year} : {fmtEur(data.annual_cap.invoiced_ttc)} / {fmtEur(data.annual_cap.cap)}</Badge>}
              {openReminders > 0 && <Badge className="border text-[10px] bg-[hsl(var(--brand-apricot))]/20 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-apricot))]/60"><BellRing className="h-3 w-3 mr-1" /> {openReminders} rappel(s)</Badge>}
            </div>

            <RevenueWidget socid={socid} onUnauthorized={onUnauthorized} testId="crm-client-revenue" />

            <div className="flex gap-1 overflow-x-auto pb-1 min-w-0" role="tablist" aria-label="Rubriques de la fiche">
              {SECTIONS.map((s) => (
                <button key={s.id} type="button" role="tab" aria-selected={section === s.id} onClick={() => setSection(s.id)} data-testid={`crm-client-section-${s.id}`}
                  className={`h-10 px-3 rounded-full border text-xs font-semibold whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${section === s.id ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-border text-muted-foreground hover:bg-secondary'}`}>
                  <s.icon className="h-3.5 w-3.5" /> {s.label}{counts[s.id] ? <span className={`rounded-full px-1.5 text-[10px] ${section === s.id ? 'bg-white/20' : 'bg-secondary'}`}>{counts[s.id]}</span> : null}
                </button>
              ))}
            </div>

            {section === 'suivi' && <NotesSection socid={socid} clientName={c.name} notes={notes} onChange={(next) => setData((d) => ({ ...d, notes: next }))} onUnauthorized={onUnauthorized} />}

            {section === 'interventions' && (
              <div className="space-y-3" data-testid="crm-client-interventions">
                <InterventionList title="À venir / en cours" items={data.interventions?.upcoming || []} empty="Aucune intervention planifiée." />
                <InterventionList title="Passées" items={data.interventions?.past || []} empty="Aucune intervention passée." past />
              </div>
            )}

            {section === 'devis' && (
              <div data-testid="crm-client-quotes">
                {!data.quotes?.length ? <EmptyLine>Aucun devis Green pour ce client. Créez-en un depuis la rubrique Devis.</EmptyLine> : (
                  <ul className="divide-y divide-border rounded-xl border border-border">
                    {data.quotes.map((d) => (
                      <li key={d.id} className="px-3 py-2 flex items-center gap-2">
                        <div className="min-w-0 flex-1"><p className="text-sm font-medium">{d.ref || 'Brouillon'} · {fmtEur(d.total_ttc)} TTC</p><p className="text-xs text-muted-foreground">{fmtDayYear(d.created_at)} · {d.created_by}{d.email_sent_at ? ` · envoyé le ${fmtDay(d.email_sent_at)}` : ''}{d.after_tax_credit != null ? ` · reste à charge ${fmtEur(d.after_tax_credit)}` : ''}</p></div>
                        <StatusBadge map={QUOTE_STATUS} value={d.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {section === 'factures' && (
              <div data-testid="crm-client-invoices">
                {!data.invoices?.items?.length ? <EmptyLine>Aucune facture validée.</EmptyLine> : (
                  <ul className="divide-y divide-border rounded-xl border border-border">
                    {data.invoices.items.map((i) => (
                      <li key={i.id} className="px-3 py-2 flex items-center gap-2">
                        <div className="min-w-0 flex-1"><p className="text-sm font-medium">{i.ref} · {fmtEurCents(i.total_ttc)}</p><p className="text-xs text-muted-foreground">{fmtDayYear(i.date)}{i.due_date && !i.paid ? ` · échéance ${fmtDay(i.due_date)}` : ''}</p></div>
                        <Badge className={`border text-[10px] ${i.state === 'paid' ? 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30' : i.state === 'overdue' ? 'bg-destructive/10 text-destructive border-destructive/30' : 'bg-[hsl(var(--brand-apricot))]/20 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-apricot))]/60'}`}>{i.state === 'paid' ? 'Payée' : i.state === 'overdue' ? `En retard · ${fmtEurCents(i.remain_to_pay)}` : `À payer · ${fmtEurCents(i.remain_to_pay)}`}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {section === 'qualite' && (
              <div className="space-y-3" data-testid="crm-client-quality">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><ClipboardCheck className="h-3.5 w-3.5" /> Contrôles qualité</p>
                  {!data.controls?.length ? <EmptyLine>Aucun contrôle qualité chez ce client.</EmptyLine> : (
                    <ul className="divide-y divide-border rounded-xl border border-border">
                      {data.controls.map((k) => (
                        <li key={k.id}><button type="button" onClick={() => setQcOpen(k.id)} className="w-full px-3 py-2 flex items-center gap-2 text-left min-h-[44px]" data-testid={`crm-client-qc-${k.id}`}>
                          <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{k.label}</p><p className="text-xs text-muted-foreground">{fmtDayYear(k.created_at)} · {k.easer_names?.join(', ')} · {k.manager_name}{k.share_with_client ? ' · partagé client' : ''}</p></div>
                          <span className="text-sm font-semibold">{fmtAvg(k.average)}</span><StarRating value={k.average} size="h-3.5 w-3.5" />
                        </button></li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><Star className="h-3.5 w-3.5" /> Avis laissés par le client</p>
                  {!data.ratings?.length ? <EmptyLine>Pas encore d'avis de ce client.</EmptyLine> : (
                    <ul className="divide-y divide-border rounded-xl border border-border">
                      {data.ratings.map((r) => (
                        <li key={r.id} className="px-3 py-2"><div className="flex items-center justify-between gap-2"><p className="text-sm font-medium truncate">{r.label}</p><StarRating value={r.stars} size="h-3.5 w-3.5" /></div><p className="text-xs text-muted-foreground">{fmtDayYear(r.created_at)} · {r.easer_name}</p>{r.comment && <p className="text-sm mt-1">« {r.comment} »</p>}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {section === 'tickets' && (
              <div data-testid="crm-client-tickets">
                {!data.tickets?.length ? <EmptyLine>Aucune réclamation de ce client.</EmptyLine> : (
                  <ul className="divide-y divide-border rounded-xl border border-border">
                    {data.tickets.map((t) => (
                      <li key={t.id} className="px-3 py-2"><div className="flex items-center justify-between gap-2"><p className="text-sm font-medium truncate">{t.subject}</p><StatusBadge map={TICKET_STATE} value={t.state} fallback={t.status_label} /></div><p className="text-xs text-muted-foreground">{t.ref ? `${t.ref} · ` : ''}{t.category_label} · {fmtDateTime(t.created_at)}</p><p className="text-sm mt-1 whitespace-pre-line">{t.message}</p></li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
        <QualityDetailDialog open={!!qcOpen} controlId={qcOpen} userId={userId} canEdit onClose={() => setQcOpen(null)} onUnauthorized={onUnauthorized} onChanged={() => load()} />
      </DialogContent>
    </Dialog>
  );
}

function InterventionList({ title, items, empty, past = false }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{title}</p>
      {!items.length ? <EmptyLine>{empty}</EmptyLine> : (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {items.map((e) => (
            <li key={e.id} className="px-3 py-2">
              <div className="flex items-center justify-between gap-2"><p className="text-sm font-medium truncate">{e.label}</p>{past ? (e.signed ? <Badge className="border text-[10px] bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30"><Check className="h-3 w-3 mr-1" /> Signée</Badge> : e.done ? <Badge variant="outline" className="text-[10px]">Terminée</Badge> : <Badge variant="outline" className="text-[10px] text-muted-foreground">Non pointée</Badge>) : null}</div>
              <p className="text-xs text-muted-foreground capitalize">{fmtDate(e.start, { weekday: 'short', day: 'numeric', month: 'short' })} {fmtTime(e.start)}{e.end ? ` – ${fmtTime(e.end)}` : ''} · {e.easer_name || 'Easer ?'}{e.rating ? ` · avis ${e.rating.stars}/5` : ''}{e.photos?.length ? <span className="inline-flex items-center gap-0.5 ml-1"><Camera className="h-3 w-3" /> {e.photos.length}</span> : null}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Notes & rappels du client (partagés entre managers). */
function NotesSection({ socid, clientName, notes, onChange, onUnauthorized }) {
  const [text, setText] = useState('');
  const [remind, setRemind] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const add = async () => {
    if (!text.trim() && !remind) return toast.error('Écrivez une note ou choisissez une date de rappel');
    setSaving(true);
    try {
      const res = await easerPost(`/api/easer/crm/clients/${socid}/notes`, { text: text.trim(), remind_at: remind || null, client_name: clientName });
      onChange([res.data.note, ...notes]); setText(''); setRemind('');
      toast.success(remind ? `Rappel programmé le ${fmtDay(remind)}` : 'Note ajoutée');
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err, "Impossible d'enregistrer"));
    } finally { setSaving(false); }
  };
  const toggleDone = async (n) => {
    setBusy(n.id);
    try { const res = await easerPut(`/api/easer/crm/notes/${n.id}`, { done: !n.done }); onChange(notes.map((x) => (x.id === n.id ? res.data.note : x))); } catch (err) { toast.error(errorMessage(err, 'Mise à jour impossible')); } finally { setBusy(null); }
  };
  const remove = async (n) => {
    setBusy(n.id);
    try { await easerDelete(`/api/easer/crm/notes/${n.id}`); onChange(notes.filter((x) => x.id !== n.id)); toast.success('Note supprimée'); } catch (err) { toast.error(errorMessage(err, 'Suppression impossible')); } finally { setBusy(null); }
  };

  const open = notes.filter((n) => !n.done);
  const done = notes.filter((n) => n.done);
  return (
    <div className="space-y-3" data-testid="crm-client-notes">
      <div className="rounded-xl border border-border p-3 space-y-2">
        <Label htmlFor="crm-note-text" className="flex items-center gap-1"><StickyNote className="h-4 w-4" /> Nouvelle note ou rappel</Label>
        <Textarea id="crm-note-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Ex. : rappeler pour le devis haie, préfère les mardis matin…" data-testid="crm-note-input" />
        <div className="flex items-center gap-2">
          <div className="relative flex-1"><BellRing className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input type="date" value={remind} min={todayIso()} onChange={(e) => setRemind(e.target.value)} className="pl-9 h-11" aria-label="Date de rappel" data-testid="crm-note-remind" /></div>
          <Button onClick={add} disabled={saving} className="h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="crm-note-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} <span className="ml-1">Ajouter</span></Button>
        </div>
      </div>
      {!notes.length && <EmptyLine testId="crm-notes-empty">Aucune note pour ce client : notez ici ce que l'équipe doit savoir, les managers la verront aussi.</EmptyLine>}
      {open.length > 0 && (
        <ul className="space-y-2" data-testid="crm-notes-open">
          {open.map((n) => <NoteRow key={n.id} n={n} busy={busy === n.id} onDone={() => toggleDone(n)} onDelete={() => remove(n)} />)}
        </ul>
      )}
      {done.length > 0 && (
        <details className="text-sm"><summary className="cursor-pointer text-muted-foreground py-1">{done.length} note(s) traitée(s)</summary>
          <ul className="space-y-2 mt-2">{done.map((n) => <NoteRow key={n.id} n={n} busy={busy === n.id} onDone={() => toggleDone(n)} onDelete={() => remove(n)} />)}</ul>
        </details>
      )}
    </div>
  );
}

function NoteRow({ n, busy, onDone, onDelete }) {
  return (
    <li className={`rounded-xl border p-3 flex items-start gap-2 ${n.overdue ? 'border-destructive/40 bg-destructive/5' : n.due_today ? 'border-[hsl(var(--brand-apricot))]/60 bg-[hsl(var(--brand-apricot))]/10' : 'border-border'} ${n.done ? 'opacity-60' : ''}`} data-testid={`crm-note-${n.id}`}>
      <button type="button" onClick={onDone} disabled={busy} aria-label={n.done ? 'Rouvrir' : 'Marquer comme fait'} className={`h-7 w-7 mt-0.5 rounded-full border flex items-center justify-center flex-shrink-0 ${n.done ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white' : 'border-border hover:bg-secondary'}`} data-testid={`crm-note-done-${n.id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : n.done ? <Check className="h-3.5 w-3.5" /> : null}</button>
      <div className="min-w-0 flex-1">
        {n.text && <p className={`text-sm whitespace-pre-line ${n.done ? 'line-through' : ''}`}>{n.text}</p>}
        <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-2">
          {n.remind_at && <span className={`inline-flex items-center gap-1 ${n.overdue && !n.done ? 'text-destructive font-medium' : ''}`}><BellRing className="h-3 w-3" /> {n.overdue && !n.done ? 'En retard · ' : n.due_today ? "Aujourd'hui · " : 'Rappel '}{fmtDayYear(n.remind_at)}</span>}
          <span>{n.author_name} · {fmtDateTime(n.created_at)}</span>
          {n.done && n.done_by && <span>fait par {n.done_by}</span>}
        </p>
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={onDelete} disabled={busy} aria-label="Supprimer" data-testid={`crm-note-delete-${n.id}`}><Trash2 className="h-4 w-4" /></Button>
    </li>
  );
}
